import { isLogoutPending } from '@/lib/auth/logout-pending';
import { createCrossTabRefresh } from '@/lib/auth/refresh-lock';
import { getAccessToken, setAccessToken } from '@/lib/auth/token-store';
import { env } from '@/lib/env';
import { traceHeaders } from '@/lib/observability/tracing';
import { ApiError } from './errors';
import { TOR_BLOCKED_CODE, isTorBlockedResponse, markTorBlocked } from './tor-block';

const BASE = env.VITE_API_URL;
const CORRELATION_HEADER = 'x-correlation-id';

interface RequestOptions {
  /** Extra headers (e.g. idempotency-key). */
  headers?: Record<string, string>;
  /** Skip the 401→refresh→retry (used by the refresh call itself). */
  noRetry?: boolean;
  /** AbortSignal for cancellation (TanStack Query passes one). */
  signal?: AbortSignal | undefined;
  /** Let the request outlive the page (`fetch` keepalive), e.g. the last answer
   *  save on pagehide. The browser caps keepalive bodies at about 64 KiB. */
  keepalive?: boolean;
}

// Single-flight refresh: many concurrent 401s trigger ONE refresh, not a stampede.
let refreshing: Promise<boolean> | null = null;

/**
 * A refresh runs while holding the cross-tab lock, so a hung one would stall
 * every tab's refresh: past this it is aborted and counts as a failed refresh.
 */
export const REFRESH_TIMEOUT_MS = 15_000;

async function rotateRefreshCookie(): Promise<string | null> {
  try {
    const signal = typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(REFRESH_TIMEOUT_MS) : undefined;
    const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include', ...(signal ? { signal } : {}) });
    if (!res.ok) {
      // Boot's silent refresh: a Tor user must get the block page, not the login screen.
      if (await isTorBlockedResponse(res)) markTorBlocked();
      return null;
    }
    const data = (await res.json()) as { accessToken?: string };
    return data.accessToken ?? null;
  } catch {
    return null;
  }
}

// ...and across tabs: one rotation at a time per browser, so two tabs never
// present the same refresh cookie (the server would revoke the session as reuse).
const doRefresh = createCrossTabRefresh({ rotate: rotateRefreshCookie, setToken: setAccessToken });

/**
 * Exchange the httpOnly refresh cookie for a fresh in-memory access token.
 * `failingToken` is the token whose request just got a 401 (by default the one
 * in memory); a token another tab broadcast is never adopted if it is that one.
 *
 * Refused while a sign-out the server never confirmed is pending (#119 E2E D1):
 * that cookie belongs to a session the person chose to end.
 */
export function refreshSession(failingToken: string | null = getAccessToken()): Promise<boolean> {
  if (isLogoutPending()) return Promise.resolve(false);
  refreshing ??= doRefresh(failingToken).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** Logout: no tab may adopt a token broadcast before it (see refresh-lock). */
export function clearRefreshBroadcast(): void {
  doRefresh.clear();
}

/**
 * The code for an error response that carried no envelope.
 *
 * A 413 is usually refused by nginx before the API sees the request, and nginx
 * answers with an HTML page. It is still exactly the platform's
 * `PAYLOAD_TOO_LARGE`, so it gets that code rather than `UNKNOWN`; the limit
 * (`details.limitBytes`) is only known when the API itself answered.
 */
function fallbackCode(status: number): string {
  return status === 413 ? 'PAYLOAD_TOO_LARGE' : 'UNKNOWN';
}

/**
 * `Retry-After` as non-negative integer delta-seconds (R8). The HTTP-date form,
 * a missing header and anything malformed give `null`, so the retry runner
 * falls back to its default delay.
 */
function parseRetryAfter(header: string | null): number | null {
  const value = header?.trim();
  return value && /^\d+$/.test(value) ? Number(value) : null;
}

async function request<T>(method: string, path: string, body: unknown, opts: RequestOptions = {}): Promise<T> {
  let sentToken: string | null = null;
  const send = (): Promise<Response> => {
    const token = (sentToken = getAccessToken());
    // Trace headers first so the backend adopts the client trace id (correlation id);
    // caller-supplied headers win last.
    const headers: Record<string, string> = { accept: 'application/json', ...traceHeaders(), ...opts.headers };
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (token) headers['authorization'] = `Bearer ${token}`;
    return fetch(`${BASE}${path}`, {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
      ...(opts.keepalive ? { keepalive: true } : {}),
    });
  };

  let res = await send();
  if (res.status === 401 && !opts.noRetry && (await refreshSession(sentToken))) {
    res = await send();
  }

  const correlationId = res.headers.get(CORRELATION_HEADER);
  if (!res.ok) {
    const envelope = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string; details?: unknown }; correlationId?: string | null };
    if (res.status === 403 && envelope.error?.code === TOR_BLOCKED_CODE) markTorBlocked();
    throw new ApiError(
      res.status,
      envelope.error?.code ?? fallbackCode(res.status),
      envelope.error?.message ?? res.statusText,
      envelope.error?.details,
      correlationId ?? envelope.correlationId ?? null,
      parseRetryAfter(res.headers.get('retry-after')),
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Fetch binary content with the SAME credentials and refresh behaviour as a JSON
 * call, and hand back an object URL.
 *
 * This exists because an `<img src>` cannot carry an Authorization header. The
 * session is a Bearer access token — the only cookie is the refresh cookie, and
 * it is scoped to /api/v1/auth — so the browser's own request for an image is
 * anonymous. That is fine for a PUBLISHED award, whose imagery is public, and it
 * silently fails for a DRAFT, whose imagery is tenant-scoped: the element
 * requests it with no session and gets a 404 it cannot report.
 *
 * The caller owns the returned URL and must revoke it (see `useObjectUrl`).
 */
async function blobUrl(path: string, opts: RequestOptions = {}): Promise<string> {
  const send = (): Promise<Response> => {
    const token = getAccessToken();
    const headers: Record<string, string> = { ...traceHeaders(), ...opts.headers };
    if (token) headers['authorization'] = `Bearer ${token}`;
    return fetch(`${BASE}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers,
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  };

  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  if (!res.ok) {
    const correlationId = res.headers.get(CORRELATION_HEADER);
    // The body is bytes on success and an error envelope on failure, so it is
    // only parsed as JSON here.
    const envelope = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    if (res.status === 403 && envelope.error?.code === TOR_BLOCKED_CODE) markTorBlocked();
    throw new ApiError(res.status, envelope.error?.code ?? fallbackCode(res.status), envelope.error?.message ?? res.statusText, undefined, correlationId);
  }
  return URL.createObjectURL(await res.blob());
}

/** The typed API client. `credentials: 'include'` on every call so the refresh
 *  cookie flows; a 401 transparently refreshes once and retries. */
export const api = {
  get: <T>(path: string, opts?: RequestOptions): Promise<T> => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> => request<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> => request<T>('PUT', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> => request<T>('PATCH', path, body, opts),
  /** `body` is optional and usually omitted — an approval gate is identified by
   *  its attachment point (event + stage) rather than a row id, so removing one
   *  has to say which. */
  delete: <T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> => request<T>('DELETE', path, body, opts),
  /** An object URL for an authenticated binary GET; the caller revokes it. */
  blobUrl,
  refreshSession,
};
